// src/components/common/StatCard.tsx

import React from "react";
import { COMPANY_COLORS } from "../../constants/theme.constants";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  colorClass?: string;
  bgClass?: string;
  description?: string;
  isCompact?: boolean;
  isClickable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  accentColor?: string;
  iconBg?: string;
  progressWidth?: number;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  description,
  isClickable = false,
  isActive = false,
  onClick,
  accentColor = COMPANY_COLORS.secondary,
  iconBg = "#FFF8EC",
  progressWidth = 50,
}) => {
  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={[
        "rounded-xl border transition-all duration-200 select-none",
        "flex flex-col gap-0",
        isClickable ? "cursor-pointer" : "cursor-default",
        isActive
          ? "border-[#F5A623] shadow-md"
          : "border-[#E2E7F0] hover:border-[#F5A623]",
      ].join(" ")}
      style={{
        background: isActive ? COMPANY_COLORS.goldPale : COMPANY_COLORS.surface,
        padding: "16px",
      }}
    >
      {/* Top row: icon + trend badge */}
      <div className="flex items-start justify-between mb-3">
        <div
          className="flex items-center justify-center rounded-xl flex-shrink-0"
          style={{
            width: 38,
            height: 38,
            background: isActive ? "rgba(245,166,35,0.15)" : iconBg,
          }}
        >
          <Icon size={18} style={{ color: accentColor }} />
        </div>

        {isActive && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{
              background: "rgba(245,166,35,0.2)",
              color: "#8a5a00",
            }}
          >
            Active
          </span>
        )}
      </div>

      {/* Value */}
      <div
        className="text-3xl font-bold leading-none mb-1"
        style={{ color: isActive ? "#8a5a00" : COMPANY_COLORS.primary }}
      >
        {value}
      </div>

      {/* Label */}
      <div
        className="text-xs font-semibold mb-0.5"
        style={{ color: isActive ? "#8a5a00" : COMPANY_COLORS.textPrimary }}
      >
        {title}
      </div>

      {/* Description */}
      {description && (
        <div
          className="text-[11px]"
          style={{ color: COMPANY_COLORS.textMuted }}
        >
          {description}
        </div>
      )}

      {/* Progress bar */}
      <div
        className="mt-3 rounded-full overflow-hidden"
        style={{ height: 3, background: "#E2E7F0" }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(Math.max(progressWidth, 5), 100)}%`,
            background: accentColor,
          }}
        />
      </div>
    </div>
  );
};